import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, apiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { tPropType } from "@/i18n/translations";
import { LanguageSelector } from "@/components/LanguageSelector";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { ArrowLeft, Loader2, Building2, Star, Plus, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nickname: "", type: "Home", address: "", unit: "", access_notes: "", contact_person: "", contact_phone: "" };

export default function Properties() {
  const nav = useNavigate();
  const { t, te, lang } = useLanguage();
  const [props, setProps] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {..., id?}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await customerApi.get("/properties");
      setProps(data);
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    customerApi.get("/meta/options").then(({ data }) => setTypes(data.property_types || [])).catch(() => {});
    load();
  }, []);

  const save = async () => {
    if (!form.nickname.trim() || !form.address.trim()) return toast.error(t("err_address"));
    setBusy(true);
    try {
      if (form.id) await customerApi.patch(`/properties/${form.id}`, form);
      else await customerApi.post("/properties", form);
      setForm(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(te(apiError(e))); } finally { setBusy(false); }
  };
  const setDefault = async (id) => { await customerApi.post(`/properties/${id}/default`); load(); };
  const archive = async (id) => { await customerApi.post(`/properties/${id}/archive`); load(); };

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="properties-back-btn" onClick={() => nav("/profile")} className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("my_profile")}
        </button>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-primary flex items-center gap-2"><Building2 className="h-6 w-6" /> {t("manage_properties")}</h2>
          {!form && <button data-testid="property-add-btn" onClick={() => setForm({ ...EMPTY })} className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold"><Plus className="h-4 w-4" /> {t("add_property")}</button>}
        </div>

        {form && (
          <div className="mt-5 rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="property-form">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-primary">{form.id ? t("edit_property") : t("add_property")}</p>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <Field label={t("property_nickname")}><input data-testid="prop-nickname" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className="input-lg" /></Field>
            <Field label={t("property_type")}>
              <select data-testid="prop-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-14 rounded-2xl border border-input bg-white px-4 focus:ring-2 focus:ring-primary focus:outline-none">
                {types.map((tp) => <option key={tp} value={tp}>{tPropType(tp, lang)}</option>)}
              </select>
            </Field>
            <Field label={t("job_address")}><AddressAutocomplete value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="prop-address" /></Field>
            <Field label={t("unit_shop")}><input data-testid="prop-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="input-lg" /></Field>
            <Field label={t("access_notes")}><input data-testid="prop-access" value={form.access_notes} onChange={(e) => setForm({ ...form, access_notes: e.target.value })} className="input-lg" /></Field>
            <Field label={t("contact_person")}><input data-testid="prop-contact-person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="input-lg" /></Field>
            <Field label={t("contact_phone")}><input data-testid="prop-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="input-lg" /></Field>
            <button data-testid="property-save-btn" onClick={save} disabled={busy} className="btn-primary">{busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("save")}</button>
          </div>
        )}

        {loading ? <p className="text-muted-foreground mt-6">{t("loading")}</p> : (
          <div className="mt-6 space-y-3">
            {props.filter((p) => !p.archived).length === 0 && !form && <p className="text-muted-foreground">{t("no_properties")}</p>}
            {props.map((p) => (
              <div key={p.id} data-testid={`property-card-${p.id}`} className={`rounded-2xl border p-4 ${p.archived ? "border-border bg-muted/40 opacity-70" : "border-border bg-white"} ${p.is_default ? "ring-2 ring-primary" : ""}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-primary flex items-center gap-2">
                    {p.nickname}
                    {p.is_default && <span className="flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[11px] text-primary-foreground"><Star className="h-3 w-3" /> {t("default_label")}</span>}
                    {p.archived && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{t("archived_label")}</span>}
                  </p>
                  <span className="text-xs text-muted-foreground">{tPropType(p.type, lang)}</span>
                </div>
                <p className="text-sm mt-1">{p.unit ? `${p.unit}, ` : ""}{p.address}</p>
                {p.access_notes && <p className="text-xs text-muted-foreground mt-1">{p.access_notes}</p>}
                {!p.archived && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <button data-testid={`prop-edit-${p.id}`} onClick={() => setForm({ id: p.id, nickname: p.nickname, type: p.type, address: p.address, unit: p.unit || "", access_notes: p.access_notes || "", contact_person: p.contact_person || "", contact_phone: p.contact_phone || "" })} className="rounded-full border border-border px-3 py-1.5 font-medium">{t("edit_property")}</button>
                    {!p.is_default && <button data-testid={`prop-default-${p.id}`} onClick={() => setDefault(p.id)} className="rounded-full border border-border px-3 py-1.5 font-medium">{t("set_default")}</button>}
                    <button data-testid={`prop-archive-${p.id}`} onClick={() => archive(p.id)} className="rounded-full border border-destructive text-destructive px-3 py-1.5 font-medium">{t("archive_label")}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-sm font-semibold text-primary mb-1.5">{label}</label>{children}</div>;
}
